import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Text from "../../../../utils/Custom/text";
import CommonForm from "../../../../utils/commonComponent/commonForm";
import { CancelButton, SaveAndUpdate } from "../../../../utils/Custom/Button";
import { fetchQuery } from "../../../../utils/commonComponent/fetched";
import axiosWithToken from "../../../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL } from "../../../../global";
import { getPermissions } from "../../../Pages/rbac/hasAccess";
import { validateRequiredFields } from "../../../../utils/commonComponent/errorAlert";
import { TextFields } from "../../../../utils/Custom/custominput";
import {
  calculateContractSalary,
  calculateFulltimeSalary,
  getContractTableRows,
  getFulltimeTableRows,
  formatCurrency,
} from "./offerJoiningUtils";

export const defaultNote16 =
  "As part of your responsibilities in Project Operations & Business Development Executive, you are expected to maintain the highest standards of professional decorum across all digital and social platforms utilized for official activities. Your LinkedIn and other professional profiles must remain updated to accurately reflect your role and association with the organization. All communications, representations, and interactions through these platforms should consistently align with the company's values, code of conduct, and brand image.";

export const defaultNote17 =
  "No allowance will be paid for daily office travel. For external client visits, travel will be reimbursed as per company policy within Mumbai. Payments will be made on the 15th of the subsequent month.";

export const offerStatuses = [
  { id: 1, name: "Pending" },
  { id: 2, name: "Accepted" },
  { id: 3, name: "Rejected" },
  { id: 4, name: "Expired" },
  { id: 5, name: "Backout" },
];

export const bgvStatuses = [
  { id: 1, name: "Pending" },
  { id: 2, name: "In Progress" },
  { id: 3, name: "Cleared" },
  { id: 4, name: "Failed" },
];

export const bondPeriodOptions = [
  { id: "6", name: "6 months" },
  { id: "12", name: "12 months" },
  { id: "18", name: "18 months" },
  { id: "24", name: "24 months" },
  { id: "36", name: "36 months" },
];

const EditOfferJoining = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const navigate = useNavigate();

  const [inputStates, setInputStates] = React.useState({
    status: 1,
    offerDate: new Date().toISOString().split("T")[0],
  });

  const [pipelines, setPipelines] = React.useState([]);
  const [pipelinesRaw, setPipelinesRaw] = React.useState([]);
  const [employmentTypes, setEmploymentTypes] = React.useState([]);

  const updateInputObject = (key, value) => {
    setInputStates((prev) => ({ ...prev, [key]: value }));
  };

  const fetchNextOfferId = async () => {
    try {
      const [last] = await fetchQuery(
        `SELECT offerId FROM talentAcqOffer ORDER BY id DESC LIMIT 1`
      );
      const lastId = parseInt(last?.offerId?.match(/\d+$/)?.[0] || "0", 10);
      const nextId = `OID-${String(lastId + 1).padStart(3, "0")}`;
      updateInputObject("offerId", nextId);
    } catch (error) {
      console.error("Error fetching last Offer ID:", error);
      updateInputObject("offerId", "OID-001");
    }
  };

  const [offerStatuses, setOfferStatuses] = React.useState([]);
  const [bgvStatuses, setBgvStatuses] = React.useState([]);

  const fetchDropdownOptions = async () => {
    try {
      const masters = await fetchQuery(
        "SELECT id, type, Value FROM talentAcquisitionMaster WHERE status != -1"
      );
      const list = masters || [];
      setEmploymentTypes(list.filter((m) => m.type == 2).map((m) => ({ id: m.id, name: m.Value })));
      setOfferStatuses(list.filter((m) => m.type == 10).map((m) => ({ id: m.id, name: m.Value })));
      setBgvStatuses(list.filter((m) => m.type == 11).map((m) => ({ id: m.id, name: m.Value })));

      const existingOffers = await fetchQuery(
        "SELECT pipelineId FROM talentAcqOffer WHERE status != -1"
      );
      const usedPipelineIds = (existingOffers || [])
        .map((o) => o.pipelineId)
        .filter(Boolean);

      const pipelineList = await fetchQuery(
        `SELECT p.id, p.pipelineId as plCode, c.candidateName, c.employmentType, r.requestedId as reqCode, p.position, p.client 
         FROM talentAcqPipeline p 
         LEFT JOIN talentAcqCandidate c ON p.candId = c.id 
         LEFT JOIN talentAcqRequirement r ON p.requirementId = r.id
         WHERE p.status = 1`
      );

      setPipelinesRaw(pipelineList || []);

      const availablePipelines = (pipelineList || []).filter((p) => {
        if (params.id != 0 && inputStates.pipelineId == p.id) return true;
        return !usedPipelineIds.includes(p.id);
      });

      setPipelines(
        availablePipelines.map((p) => ({
          id: p.id,
          name: p.plCode,
        }))
      );
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  const fetchDetails = async () => {
    try {
      const [record] = await fetchQuery(
        `SELECT * FROM talentAcqOffer WHERE id = ${params.id}`
      );
      if (record) {
        if (record.offerDate) record.offerDate = record.offerDate.split("T")[0];
        if (record.offerExpiryDate) record.offerExpiryDate = record.offerExpiryDate.split("T")[0];
        if (record.confirmedJoiningDate) record.confirmedJoiningDate = record.confirmedJoiningDate.split("T")[0];
        if (record.actualJoiningDate) record.actualJoiningDate = record.actualJoiningDate.split("T")[0];
        if (record.probationEndDate) record.probationEndDate = record.probationEndDate.split("T")[0];
        setInputStates(record);
      }
    } catch (error) {
      console.error("Error fetching offer joining data:", error);
    }
  };

  const onSubmit = async () => {
    const fieldsToValidate = [
      { value: inputStates.pipelineId, label: "Pipeline ID is required" },
      { value: inputStates.offerDate, label: "Offer Date is required" },
    ];

    if (!validateRequiredFields(fieldsToValidate)) return;

    const payload = {
      ...inputStates,
      note16: inputStates.note16 !== undefined && inputStates.note16 !== null ? inputStates.note16 : defaultNote16,
      note17: inputStates.note17 !== undefined && inputStates.note17 !== null ? inputStates.note17 : defaultNote17,
    };
    if (isContract) {
      payload.basicSalary = null;
      payload.houseAllow = null;
      payload.educationAllow = null;
      payload.medicalReimb = null;
      payload.conveyance = null;
      payload.personalAllow = null;
      payload.cityCompAllow = null;
      payload.otherAllow = null;
      payload.totalMonthly = null;
      payload.subTotalA = null;
      payload.companyPF = null;
      payload.companyGratuity = null;
      payload.annualHealthCheck = null;
      payload.perfoIncentive = null;
      payload.subTotalB = null;
      payload.variableAllowance = null;
    } else {
      payload.contractFees = null;
      payload.contractOtherAllow = null;
      payload.mobileConnect = null;
      payload.medical = null;
      payload.contractTotalMon = null;
    }

    try {
      const url = BASE_URL + `talentAcqOffer`;
      params.id != 0
        ? await axiosWithToken.patch(`${url}/${params.id}`, payload)
        : await axiosWithToken.post(url, payload);

      toast.success(
        params.id != 0 ? "Offer updated successfully!" : "Offer created successfully!"
      );
      if (params.id == 0) {
        setTimeout(() => navigate("/talent-acquisition/offers"), 1000);
      } else {
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  useEffect(() => {
    fetchDropdownOptions();
  }, [params.id, inputStates.pipelineId]);

  useEffect(() => {
    if (params.id == 0) fetchNextOfferId();
  }, []);

  useEffect(() => {
    if (params.id != 0) fetchDetails();
  }, [params]);

  useEffect(() => {
    if (inputStates.pipelineId) {
      const pipeObj = pipelinesRaw.find((p) => p.id == inputStates.pipelineId);
      if (pipeObj) {
        if (
          inputStates.candidateName !== pipeObj.candidateName ||
          inputStates.requId !== pipeObj.reqCode ||
          inputStates.position !== pipeObj.position ||
          inputStates.employmentType !== pipeObj.employmentType ||
          inputStates.client !== pipeObj.client
        ) {
          const empTypeObj = employmentTypes.find((m) => m.id == pipeObj.employmentType);
          const pipeIsContract = empTypeObj && empTypeObj.name ? empTypeObj.name.toLowerCase().includes("contract") : false;

          setInputStates((prev) => {
            const updated = {
              ...prev,
              candidateName: pipeObj.candidateName || "",
              requId: pipeObj.reqCode || "",
              position: pipeObj.position || "",
              employmentType: pipeObj.employmentType || "",
              client: pipeObj.client || "",
            };

            if (pipeIsContract) {
              updated.basicSalary = null;
              updated.houseAllow = null;
              updated.educationAllow = null;
              updated.medicalReimb = null;
              updated.conveyance = null;
              updated.personalAllow = null;
              updated.cityCompAllow = null;
              updated.otherAllow = null;
              updated.totalMonthly = null;
              updated.subTotalA = null;
              updated.companyPF = null;
              updated.companyGratuity = null;
              updated.annualHealthCheck = null;
              updated.perfoIncentive = null;
              updated.subTotalB = null;
              updated.variableAllowance = null;
            } else {
              updated.contractFees = null;
              updated.contractOtherAllow = null;
              updated.mobileConnect = null;
              updated.medical = null;
              updated.contractTotalMon = null;
            }

            return updated;
          });
        }
      }
    }
  }, [inputStates.pipelineId, pipelinesRaw, employmentTypes]);

  const isContract = React.useMemo(() => {
    const empTypeObj = employmentTypes.find((m) => m.id == inputStates.employmentType);
    if (empTypeObj && empTypeObj.name) {
      return empTypeObj.name.toLowerCase().includes("contract");
    }
    return false;
  }, [inputStates.employmentType, employmentTypes]);

  useEffect(() => {
    if (isContract) {
      const cCalc = calculateContractSalary(inputStates);
      if (
        inputStates.contractTotalMon !== cCalc.cTotalM ||
        inputStates.ctcOffered !== cCalc.cTotalY
      ) {
        setInputStates((prev) => ({
          ...prev,
          contractTotalMon: cCalc.cTotalM,
          ctcOffered: cCalc.cTotalY,
        }));
      }
    } else {
      const fCalc = calculateFulltimeSalary(inputStates);
      if (
        inputStates.subTotalA !== fCalc.subAM ||
        inputStates.companyGratuity !== fCalc.gratuityY ||
        inputStates.subTotalB !== fCalc.subBY ||
        inputStates.ctcOffered !== fCalc.totalY
      ) {
        setInputStates((prev) => ({
          ...prev,
          subTotalA: fCalc.subAM,
          companyGratuity: fCalc.gratuityY,
          subTotalB: fCalc.subBY,
          ctcOffered: fCalc.totalY,
        }));
      }
    }
  }, [
    isContract,
    inputStates.contractFees,
    inputStates.contractOtherAllow,
    inputStates.mobileConnect,
    inputStates.medical,
    inputStates.basicSalary,
    inputStates.houseAllow,
    inputStates.educationAllow,
    inputStates.medicalReimb,
    inputStates.conveyance,
    inputStates.personalAllow,
    inputStates.cityCompAllow,
    inputStates.otherAllow,
    inputStates.companyPF,
    inputStates.annualHealthCheck,
    inputStates.perfoIncentive,
  ]);

  const contractTableRows = getContractTableRows(inputStates);
  const fulltimeTableData = getFulltimeTableRows(inputStates);
  const contractCalc = calculateContractSalary(inputStates);

  const commonFields = [
    {
      type: "TextField",
      label: "Offer ID *",
      stateKey: "offerId",
      disabled: true,
    },
    {
      type: "Autocomplete",
      label: "Pipeline *",
      stateKey: "pipelineId",
      options: pipelines,
      valueKey: "id",
      getOptionLabelKey: "name",
      disabled: params.id != 0,
    },
    {
      type: "TextField",
      label: "Candidate Name",
      stateKey: "candidateName",
      disabled: true,
    },
    {
      type: "TextField",
      label: "Req ID",
      stateKey: "requId",
      disabled: true,
    },
    {
      type: "TextField",
      label: "Position",
      stateKey: "position",
      disabled: true,
    },
    {
      type: "Autocomplete",
      label: "Employment Type",
      stateKey: "employmentType",
      options: employmentTypes,
      valueKey: "id",
      getOptionLabelKey: "name",
      disabled: true,
    },
    {
      type: "TextField",
      label: "Client",
      stateKey: "client",
      disabled: true,
    },
    {
      type: "Autocomplete",
      label: "Bond Period",
      stateKey: "bondPeriod",
      options: bondPeriodOptions,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "TextField",
      label: "Bond Amount",
      stateKey: "bondAmount",
      inputType: "number",
    },
    {
      type: "DatePicker",
      label: "Offer Date *",
      stateKey: "offerDate",
    },
    {
      type: "DatePicker",
      label: "Offer Expiry Date",
      stateKey: "offerExpiryDate",
    },
    {
      type: "Autocomplete",
      label: "Offer Status",
      stateKey: "offerStatus",
      options: offerStatuses,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "DatePicker",
      label: "Confirmed Joining Date",
      stateKey: "confirmedJoiningDate",
    },
    {
      type: "DatePicker",
      label: "Actual Joining Date",
      stateKey: "actualJoiningDate",
    },
    {
      type: "Autocomplete",
      label: "BGV Status",
      stateKey: "bgvStatus",
      options: bgvStatuses,
      valueKey: "id",
      getOptionLabelKey: "name",
    },
    {
      type: "DatePicker",
      label: "Probation End Date",
      stateKey: "probationEndDate",
    },
    {
      type: "TextField",
      label: "Post-Joining Status",
      stateKey: "PostJoiningStatus",
    },
  ];

  const contractSalaryFields = [
    {
      type: "TextField",
      label: "Contract Fees (Monthly)",
      stateKey: "contractFees",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Other Allowances (Monthly)",
      stateKey: "contractOtherAllow",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Telephone/Mobile/R Connect Expense (Monthly)",
      stateKey: "mobileConnect",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Medical (Monthly)",
      stateKey: "medical",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Contract Total Monthly",
      stateKey: "contractTotalMon",
      inputType: "number",
      disabled: true,
    },
    {
      type: "TextField",
      label: "CTC Offered (Total)",
      stateKey: "ctcOffered",
      inputType: "number",
      disabled: true,
    },
  ];

  const regularSalaryFields = [
    {
      type: "TextField",
      label: "Basic Salary (Monthly)",
      stateKey: "basicSalary",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "House Rent Allowance (Monthly)",
      stateKey: "houseAllow",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Education Allowance (Monthly)",
      stateKey: "educationAllow",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Medical Reimbursement (Monthly)",
      stateKey: "medicalReimb",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Conveyance (Monthly)",
      stateKey: "conveyance",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Personal Allowance (Monthly)",
      stateKey: "personalAllow",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "City Compensatory Allowance (Monthly)",
      stateKey: "cityCompAllow",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Other Allowance (Monthly)",
      stateKey: "otherAllow",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Sub Total (A) (Monthly)",
      stateKey: "subTotalA",
      inputType: "number",
      disabled: true,
    },
    {
      type: "TextField",
      label: "Company's Contribution to P.F. (Monthly)",
      stateKey: "companyPF",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Company's Contribution to Gratuity (4.81%)",
      stateKey: "companyGratuity",
      inputType: "number",
      disabled: true,
    },
    {
      type: "TextField",
      label: "Annual Health checkup (Yearly)",
      stateKey: "annualHealthCheck",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Performance Incentive (Yearly)",
      stateKey: "perfoIncentive",
      inputType: "number",
    },
    {
      type: "TextField",
      label: "Sub Total B (Yearly)",
      stateKey: "subTotalB",
      inputType: "number",
      disabled: true,
    },
    {
      type: "TextField",
      label: "CTC Offered (Total)",
      stateKey: "ctcOffered",
      inputType: "number",
      disabled: true,
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <Text variant="h3" bold className="text-blue-600">
          {params.id == 0 ? "Create Offer & Joining" : "Update Offer & Joining"}
        </Text>
      </div>
      <br />

      {/* Main Details Section */}
      <CommonForm
        inputStates={inputStates}
        updateInputObject={updateInputObject}
        inputFields={commonFields}
      />
      <br />

      {/* Notes / Terms & Conditions Section */}
      {!isContract && (
        <>
          <div className="border-t pt-4 mt-2">
            <Text variant="h4" bold className="text-gray-800 mb-3">
              Offer Letter Notes
            </Text>
            <div className="grid grid-cols-1 gap-4">
              <TextFields
                id="note16"
                multiline
                rows={3}
                label="Offer Letter Note (Point 16)"
                value={inputStates.note16 !== undefined && inputStates.note16 !== null ? inputStates.note16 : defaultNote16}
                onChange={(e) => updateInputObject("note16", e.target.value)}
              />
              <TextFields
                id="note17"
                multiline
                rows={2}
                label="Offer Letter Note (Point 17)"
                value={inputStates.note17 !== undefined && inputStates.note17 !== null ? inputStates.note17 : defaultNote17}
                onChange={(e) => updateInputObject("note17", e.target.value)}
              />
            </div>
          </div>
          <br />
        </>
      )}

      {/* Salary Details Section */}
      <div className="border-t pt-4 mt-2">
        <Text variant="h4" bold className="text-gray-800 mb-3">
          {isContract ? "Contract Fee Details" : "Salary Details"}
        </Text>
        <CommonForm
          inputStates={inputStates}
          updateInputObject={updateInputObject}
          inputFields={isContract ? contractSalaryFields : regularSalaryFields}
        />
      </div>
      <br />

      {/* Dynamic  table rendering */}
      <div className="mt-6 border border-gray-400 rounded-md p-4 bg-white shadow-sm max-w-4xl mx-auto">
        <h3 className="text-center text-lg font-bold underline mb-4 text-gray-900">
          {isContract ? "CTC Annexure A – Contract Fees Break-up" : "CTC Annexure A - Salary Break-up"}
        </h3>

        {isContract ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-500 text-sm">
              <thead>
                <tr className="bg-gray-100 font-bold text-gray-800">
                  <th className="border border-gray-500 px-4 py-2 text-left w-1/2">Monthly Allowances (A)</th>
                  <th className="border border-gray-500 px-4 py-2 text-right w-1/4">Monthly</th>
                  <th className="border border-gray-500 px-4 py-2 text-right w-1/4">Yearly</th>
                </tr>
              </thead>
              <tbody className="text-gray-800">
                {contractTableRows.map((row, i) => (
                  <tr key={i}>
                    <td className="border border-gray-500 px-4 py-1.5">{row.label}</td>
                    <td className="border border-gray-500 px-4 py-1.5 text-right">{formatCurrency(row.monthly)}</td>
                    <td className="border border-gray-500 px-4 py-1.5 text-right">{formatCurrency(row.yearly)}</td>
                  </tr>
                ))}
                <tr className="font-bold text-base bg-gray-50">
                  <td className="border border-gray-500 px-4 py-2">Total (Monthly)</td>
                  <td className="border border-gray-500 px-4 py-2 text-right">{formatCurrency(contractCalc.cTotalM)}</td>
                  <td className="border border-gray-500 px-4 py-2 text-right">{formatCurrency(contractCalc.cTotalY)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-500 text-sm">
              <thead>
                <tr className="bg-gray-100 font-bold text-gray-800">
                  <th className="border border-gray-500 px-4 py-2 text-left w-1/2">Particulars</th>
                  <th className="border border-gray-500 px-4 py-2 text-right w-1/4">Monthly</th>
                  <th className="border border-gray-500 px-4 py-2 text-right w-1/4">Yearly</th>
                </tr>
              </thead>
              <tbody className="text-gray-800">
                {fulltimeTableData.subA.map((row, i) => (
                  <tr key={`subA-${i}`}>
                    <td className="border border-gray-500 px-4 py-1.5">{row.label}</td>
                    <td className="border border-gray-500 px-4 py-1.5 text-right">{formatCurrency(row.monthly)}</td>
                    <td className="border border-gray-500 px-4 py-1.5 text-right">{formatCurrency(row.yearly)}</td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td className="border border-gray-500 px-4 py-1.5">Sub Total (A)</td>
                  <td className="border border-gray-500 px-4 py-1.5 text-right">{formatCurrency(fulltimeTableData.totals.subAM)}</td>
                  <td className="border border-gray-500 px-4 py-1.5 text-right">{formatCurrency(fulltimeTableData.totals.subAY)}</td>
                </tr>
                {fulltimeTableData.subB.map((row, i) => (
                  <tr key={`subB-${i}`}>
                    <td className="border border-gray-500 px-4 py-1.5">{row.label}</td>
                    <td className="border border-gray-500 px-4 py-1.5 text-right">
                      {row.monthlyLabel ? row.monthlyLabel : formatCurrency(row.monthly)}
                    </td>
                    <td className="border border-gray-500 px-4 py-1.5 text-right">{formatCurrency(row.yearly)}</td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td className="border border-gray-500 px-4 py-1.5">Sub Total B</td>
                  <td className="border border-gray-500 px-4 py-1.5 text-right"></td>
                  <td className="border border-gray-500 px-4 py-1.5 text-right">{formatCurrency(fulltimeTableData.totals.subBY)}</td>
                </tr>
                <tr className="font-bold text-base bg-gray-50">
                  <td className="border border-gray-500 px-4 py-2">Total (A+B)</td>
                  <td className="border border-gray-500 px-4 py-2 text-right"></td>
                  <td className="border border-gray-500 px-4 py-2 text-right">{formatCurrency(fulltimeTableData.totals.totalY)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex gap-10 mt-5 justify-end">
        <CancelButton
          id="btn-cancel"
          label="Cancel"
          outline
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        />
        {(permissions.save || permissions.allAccess) && (
          <SaveAndUpdate
            id="btn-save"
            label={params.id == 0 ? "Save" : "Update"}
            onClick={onSubmit}
          />
        )}
      </div>
    </div>
  );
};

export default EditOfferJoining;
